import React, { useEffect, useRef, useState } from "react";
import "../css/Layout.css"; // Optional: Include any additional styles
import { useLocation, useNavigate } from "react-router-dom";
import DeviceLayout from "./DeviceLayout";

const DeviceEvents = () => {
    const [data, setData] = useState([]);
    const [error, setError] = useState(null);
    const [page, setPage] = useState(1); // Track the current page
    const [loading, setLoading] = useState(true);

    const limit = 20; // Number of records to fetch per page
    const location = useLocation();
    const navigate = useNavigate();
    const highlightRef = useRef(null);
    const highlightResolvedRef = useRef(false);

    const getHighlightFromUrl = () => {
        const params = new URLSearchParams(location.search);
        return params.get("highlight");
    };
    const highlight = getHighlightFromUrl();

    const isMatchingHighlight = (record, highlightValue) => {
        if (!record || !record.received_at || !highlightValue) return false;
        if (String(record.received_at) === String(highlightValue)) return true;

        const recordTs = new Date(record.received_at).getTime();
        const highlightTs = new Date(highlightValue).getTime();
        return Number.isFinite(recordTs) && Number.isFinite(highlightTs) && recordTs === highlightTs;
    };

    // Function to extract device ID from the URL
    const getDeviceIdFromUrl = () => {
        const params = new URLSearchParams(location.search);
        return params.get("id");
    };

    useEffect(() => {
        highlightResolvedRef.current = false;
    }, [highlight]);

    // Fetch data whenever the component mounts or the page changes
    useEffect(() => {
        fetchData();
    }, [page, highlight]);

    const fetchEventsPage = async (id, pageNumber) => {
        const url = `/api/device/events?id=${id}&limit=${limit}&page=${pageNumber}`;
        const response = await fetch(url, { credentials: "include" });
        if (!response.ok) {
            throw new Error(`Error: ${response.statusText}`);
        }
        const pageData = await response.json();
        return Array.isArray(pageData) ? pageData : [];
    };

    const findPageForHighlight = async (id, highlightValue) => {
        const maxPagesToScan = 200;
        let lastPageData = [];

        for (let pageNumber = 1; pageNumber <= maxPagesToScan; pageNumber += 1) {
            const pageData = await fetchEventsPage(id, pageNumber);
            lastPageData = pageData;

            if (pageData.some((record) => isMatchingHighlight(record, highlightValue))) {
                return { pageNumber, pageData, found: true };
            }

            if (pageData.length < limit) {
                break;
            }
        }

        return { pageNumber: 1, pageData: lastPageData, found: false };
    };

    const fetchData = async () => {
        const id = getDeviceIdFromUrl();
        if (id) {
            try {
                setLoading(true);
                setError(null); // Clear any previous errors

                if (highlight && !highlightResolvedRef.current) {
                    const result = await findPageForHighlight(id, highlight);
                    highlightResolvedRef.current = true;

                    if (result.found && result.pageNumber !== page) {
                        setPage(result.pageNumber);
                    }

                    setData(result.pageData);
                } else {
                    const pageData = await fetchEventsPage(id, page);
                    setData(pageData);
                }
            } catch (error) {
                setError("Error fetching device data");
                console.error("Error fetching device data:", error);
            } finally {
                setLoading(false);
            }
        } else {
            setError("Device ID not found in the URL.");
            console.error("Device ID not found in the URL.");
            setLoading(false);
        }
    };

    // Handle pagination
    const handleNextPage = () => {
        setPage(page + 1);
    };

    const handlePreviousPage = () => {
        if (page > 1) {
            setPage(page - 1);
        }
    };

    // Scroll to highlighted row after render
    useEffect(() => {
        if (highlight && highlightRef.current) {
            highlightRef.current.scrollIntoView({ behavior: "smooth", block: "center" });
        }
    }, [data, highlight]);

    const handleRowClick = (record) => {
        const id = getDeviceIdFromUrl();
        const t = new Date(record.received_at);
        const start = new Date(t.getTime() - 30 * 60 * 1000);
        const end   = new Date(t.getTime() + 30 * 60 * 1000);
        const toLocal = (d) =>
            new Date(d.getTime() - d.getTimezoneOffset() * 60000).toISOString().slice(0, 16);
        const storedData = localStorage.getItem("selectedData");
        const dataParam = storedData ? JSON.parse(storedData).join(",") : "";
        navigate(
            `/device/history?id=${id}&data=${encodeURIComponent(dataParam)}&start=${toLocal(start)}&end=${toLocal(end)}`
        );
    };

    // Function to determine the cell color based on status value
    const getStatusColor = (value) => {
        if (value === 1) {
            return "green"; // Active or OK status
        } else if (value === 2) {
            return "yellow"; // Inactive or Error status
        } else if (value === 3) {
            return "red"; // Inactive or Error status
        } else if (value === 4) {
            return "red"; // Inactive or Error status
        } else if (value === 13) {
            return "green"; // Inactive or Error status
        } else {
            return "gray"; // Unknown or default status
        }
    };

    const getStatusEvent = (value) => {
        if (value === 1) {
            return "OK"; // Active or OK status
        } else if (value === 2) {
            return "ALARM"; // Inactive or Error status
        } else if (value === 3) {
            return "PROTECT"; // Inactive or Error status
        } else if (value === 4) {
            return "ERROR"; // Inactive or Error status
        } else if (value === 6) {
            return "PRECHARGE ERROR"; // Inactive or Error status
        } else if (value === 12) {
            return "CHECKING"; // Inactive or Error status
        } else if (value === 11) {
            return "OCP"; // Inactive or Error status
        } else if (value === 13) {
            return "MEB"; // Inactive or Error status
        } else if (value === 9) {
            return "OFF"; // Inactive or Error status
        } else if (value === 8) {
            return "ON"; // Inactive or Error status
        } else if (value === 10) {
            return "PRECHARGE"; // Inactive or Error status
        } else if (value === 14) {
            return "EMERGENCY_SW"; // Inactive or Error status
        } else if (value === 15) {
            return "PDU_ERR"; // Inactive or Error status
        } else if (value === 21){
            return "PDU_NO_RESPONSE"; // Inactive or Error status
        } else if (value === 22){
            return "PDU_FAULT_COMM"; // Inactive or Error status
        } else if (value === 23){
            return "PDU_SEQ_TIMEOUT"; // Inactive or Error status
        } else if (value === 24){
            return "PDU_VOLTAGE_CHECK"; // Inactive or Error status
        } else if (value === 25){
            return "PDU_OCP"; // Inactive or Error status
        } else if (value === 26){
            return "PDU_OV"; // Inactive or Error status
        } else if (value === 27){
            return "PDU_SYSTEM_FAULT"; // Inactive or Error status
        } else if (value === 28){
            return "PDU_CONFIG_FAULT"; // Inactive or Error status
        } else if (value === 29){
            return "PDU_VOLTAGE_MISMATCH"; // Inactive or Error status
        } else if (value === 30){
            return "PDU_PROGRAMMING"; // Inactive or Error status
        } else if (value === 31){
            return "PDU_PROGRAM_OK"; // Inactive or Error status
        } else if (value === 32){
            return "PDU_PROGRAM_FAIL"; // Inactive or Error status
        } else {
            return "NO_DATA"; // Unknown or default status
        }

    };

    return (
      <DeviceLayout>
                    {loading ? (
        <div className="spinner-container"><div className="spinner"></div></div> // Replace with a spinner component or CSS loader
      ) : (
        <div>
            {error && <p style={{ color: 'red' }}>{error}</p>}
            <div>
                {data.length > 0 ? (
                    <table className="status-table">
                        <thead>
                            <tr>
                                <th>Received At</th>
                                <th>Elapsed time</th>
                                <th>BMS Master</th>
                                <th>BMS1</th>
                                <th>BMS2</th>
                                <th>CAN</th>
                                {/* <th>Relay</th> */}
                                <th>VCell Min</th>
                                <th>VCell Max</th>
                                <th>dV</th>
                                <th>Temp Min</th>
                                <th>Temp Max</th>
                                {/* <th>Modbus</th> */}
                            </tr>
                        </thead>
                        <tbody>
                            {data.map((record, index) => (
                                <tr
                                    key={index}
                                    ref={isMatchingHighlight(record, highlight) ? highlightRef : null}
                                    onClick={() => handleRowClick(record)}
                                    style={{
                                        cursor: "pointer",
                                        ...(isMatchingHighlight(record, highlight)
                                            ? { outline: "2px solid #f55", backgroundColor: "rgba(255,80,80,0.18)" }
                                            : {}),
                                    }}
                                >
                                    <td>{new Date(record.received_at).toLocaleString()}</td>
                                    <td>{record.elapsed_time}</td>
                                    <td style={{ backgroundColor: getStatusColor(record.BMSmaster) }}>{getStatusEvent(record.BMSmaster)}</td>
                                    <td style={{ backgroundColor: getStatusColor(record.BMS1) }}>{getStatusEvent(record.BMS1)}</td>
                                    <td style={{ backgroundColor: getStatusColor(record.BMS2) }}>{getStatusEvent(record.BMS2)}</td>
                                    <td style={{ backgroundColor: getStatusColor(record.CAN) }}>{getStatusEvent(record.CAN)}</td>
                                    {/* <td style={{ backgroundColor: getStatusColor(record.rel) }}>{getStatusEvent(record.rel)}</td> */}
                                    <td style={{ backgroundColor: getStatusColor(record.VCellMin) }}>{getStatusEvent(record.VCellMin)}</td>
                                    <td style={{ backgroundColor: getStatusColor(record.VCellMax) }}>{getStatusEvent(record.VCellMax)}</td>
                                    <td style={{ backgroundColor: getStatusColor(record.dV) }}>{getStatusEvent(record.dV)}</td>
                                    <td style={{ backgroundColor: getStatusColor(record.TempMin) }}>{getStatusEvent(record.TempMin)}</td>
                                    <td style={{ backgroundColor: getStatusColor(record.TempMax) }}>{getStatusEvent(record.TempMax)}</td>
                                    {/* <td style={{ backgroundColor: getStatusColor(record.Modbus) }}>{getStatusEvent(record.Modbus)}</td> */}
                                </tr>
                            ))}
                        </tbody>
                    </table>
                ) : (
                    <p>No data available</p>
                )}
            </div>
            <div className="topdevice">
                <button onClick={handlePreviousPage} disabled={page === 1}>
                    Previous
                </button>
                <span> Page {page} </span>
                <button onClick={handleNextPage}>
                    Next
                </button>
            </div>
        </div>
      )}
      </DeviceLayout>
    );
};

export default DeviceEvents;
